import { ActionTree } from 'vuex';
import { RootState } from '@/store/types';
import { AdsState, AdsActions, AdsFilters } from './types';
import { AdsMutations } from './types';
import Ad, { AdStatus } from './ad';
import { AdJSON } from './ad';
import { ListStatus, EditableStatus } from '@/store/types';
import { RootActions } from '@/store/types';
import { ErrorMessages } from '@/store/types';
import { enhanceErrorWithMessage } from '@/utils/errors';
import api from '@/api';
import router from '@/router';
import i18next from '@/i18n';
import { NotificationsActions } from '@/store/modules/notifications/types';
export const actions: ActionTree<AdsState, RootState> = {
  [AdsActions.LOAD_ALL]({ commit, dispatch }, filters = {}): Promise<void> {
    commit(AdsMutations.SET_ALL_STATUS, ListStatus.Loading);

    return api.ads
      .list(filters)
      .then((response) => response.data)
      .then((response) => {
        const ads = response.data.map((ad: AdJSON) => Ad.fromJSON(ad));
        commit(AdsMutations.SET_ALL, ads);
        commit(AdsMutations.SET_LIST_PAGINATION, {
          currentPage: response.current_page,
          lastPage: response.last_page,
          total: response.total,
        });
        commit(AdsMutations.SET_ALL_STATUS, ListStatus.Loaded);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        commit(AdsMutations.SET_ALL_STATUS, ListStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [AdsActions.LOAD_EDITABLE]({ commit, dispatch }, { id }): Promise<void> {
    commit(AdsMutations.SET_EDITABLE_STATUS, EditableStatus.Loading);

    if (!id) {
      const ad = new Ad();
      ad.status = AdStatus.Active;
      commit(AdsMutations.SET_EDITABLE, ad);
      commit(AdsMutations.SET_EDITABLE_STATUS, EditableStatus.Loaded);
      return Promise.resolve();
    }

    return api.ads
      .get(id)
      .then((response) => response.data)
      .then((response) => {
        const ad = Ad.fromJSON(response);
        commit(AdsMutations.SET_EDITABLE, ad);
        commit(AdsMutations.SET_EDITABLE_STATUS, EditableStatus.Loaded);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        commit(AdsMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [AdsActions.UPDATE_EDITABLE]({ commit }, changes): Promise<void> {
    commit(AdsMutations.UPDATE_EDITABLE, changes);
    return Promise.resolve();
  },

  [AdsActions.DELETE]({ commit, dispatch }, { id = 0 }: { id?: number } = {}): Promise<void> {
    const isEdit = router.currentRoute.name === 'edit-ad';
    if (isEdit) {
      commit(AdsMutations.SET_EDITABLE_STATUS, EditableStatus.Saving);
    } else {
      commit(AdsMutations.SET_ALL_STATUS, ListStatus.Loading);
    }

    return new Promise<void>((resolve, reject) => {
      if (!id) {
        return reject(new Error('Invalid data!'));
      }

      resolve();
    })
      .then(() => api.ads.remove(id))
      .then(() => {
        if (isEdit) {
          commit(AdsMutations.UPDATE_INITIAL_EDITABLE, {
            status: AdStatus.Inactive,
          });
          commit(AdsMutations.UPDATE_EDITABLE, {
            status: AdStatus.Inactive,
          });
          commit(AdsMutations.SET_EDITABLE_STATUS, EditableStatus.Saved);
        } else {
          dispatch(AdsActions.LOAD_ALL, {
            ...router.currentRoute.params,
            ...router.currentRoute.query,
          });
        }
      })
      .then(() => {
        return dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('The ad was deleted successfully.'),
          },
          { root: true },
        );
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        if (isEdit) {
          commit(AdsMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        } else {
          commit(AdsMutations.SET_ALL_STATUS, ListStatus.Failed);
        }
        
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [AdsActions.RESTORE]({ commit, dispatch }, { id = 0 }: { id?: number } = {}): Promise<void> {
    const isEdit = router.currentRoute.name === 'edit-ad';
    if (isEdit) {
      commit(AdsMutations.SET_EDITABLE_STATUS, EditableStatus.Saving);
    } else {
      commit(AdsMutations.SET_ALL_STATUS, ListStatus.Loading);
    }

    return new Promise<void>((resolve, reject) => {
      if (!id) {
        return reject(new Error('Invalid data!'));
      }

      resolve();
    })
      .then(() => api.ads.restore(id))
      .then(() => {
        if (isEdit) {
          commit(AdsMutations.UPDATE_INITIAL_EDITABLE, {
            status: AdStatus.Active,
          });
          commit(AdsMutations.UPDATE_EDITABLE, {
            status: AdStatus.Active,
          });
          commit(AdsMutations.SET_EDITABLE_STATUS, EditableStatus.Saved);
        } else {
          dispatch(AdsActions.LOAD_ALL, {
            ...router.currentRoute.params,
            ...router.currentRoute.query,
          });
        }
      })
      .then(() =>
        dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('The ad was restored successfully.'),
          },
          { root: true },
        ),
      )
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        if (isEdit) {
          commit(AdsMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        } else {
          commit(AdsMutations.SET_ALL_STATUS, ListStatus.Failed);
        }

        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [AdsActions.APPLY_FILTERS]({ dispatch }, filters: AdsFilters): Promise<void> {
    return dispatch(AdsActions.LOAD_ALL, filters);
  },

  [AdsActions.LOAD_PRODUCTS]({ commit, dispatch }): Promise<void> {
    return api.products
      .listTree()
      .then((response) => {
        commit(AdsMutations.SET_PRODUCTS, response.data);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [AdsActions.LOAD_COUNTRIES]({ commit, dispatch }): Promise<void> {
    return api.countries
      .list()
      .then((response) => {
        commit(AdsMutations.SET_COUNTRIES, response.data);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [AdsActions.SAVE]({ state, commit, dispatch }): Promise<void> {
    let isUpdate: boolean;
    let url: string;
    let successMsg: string;

    return new Promise<void>((resolve, reject) => {
      if (!state.editable) {
        return reject(new Error('Invalid data!'));
      }

      resolve();
    })
      .then(() => {
        commit(AdsMutations.SET_EDITABLE_STATUS, EditableStatus.Saving);

        isUpdate = Boolean(state.editable!.id);
        url = 'ad';
        successMsg = i18next.t('A new ad was added successfully.');

        if (isUpdate) {
          url += `/${state.editable!.id}`;
          successMsg = i18next.t('The ad was successfully updated.');
        }
      })
      .then(() => api.ads.store(state.editable!.toJSON()))
      .then((response) => response.data)
      .then((response): Promise<Ad> => {
        const ad = Ad.fromJSON(response);

        commit(AdsMutations.SET_EDITABLE, ad);
        commit(AdsMutations.SET_EDITABLE_STATUS, EditableStatus.Saved);

        if (!isUpdate) {
          return new Promise((resolve, reject) => {
            if (!ad.id) {
              return reject(new Error('Invalid data.'));
            }

            router.replace(
              {
                name: 'edit-ad',
                params: {
                  id: String(ad.id),
                },
              },
              () => resolve(ad),
              (error: any) => reject(error),
            );
          });
        }

        return Promise.resolve(ad);
      })
      .then(() => {
        return dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: successMsg,
          },
          { root: true },
        );
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        commit(AdsMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },
  [AdsActions.BULK_DELETE]({ commit, dispatch }, { ids = [] }: { ids: number[] } = { ids: [] }): Promise<void> {
    commit(AdsMutations.SET_ALL_STATUS, ListStatus.Loading);

    return new Promise<void>((resolve, reject) => {
      if (!ids || !ids.length) {
        return reject(new Error('Invalid data!'));
      }

      resolve();
    })
      .then(() => Promise.all(ids.map((id) => api.ads.remove(id))))
      .then(() => {
        dispatch(AdsActions.LOAD_ALL, {
          ...router.currentRoute.params,
          ...router.currentRoute.query,
        });

        return dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('%(count)% ad was deleted successfully', { count: ids.length }),
          },
          { root: true },
        );
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        commit(AdsMutations.SET_ALL_STATUS, ListStatus.Failed);

        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },
  [AdsActions.BULK_RESTORE]({ commit, dispatch }, { ids = [] }: { ids: number[] } = { ids: [] }): Promise<void> {
    commit(AdsMutations.SET_ALL_STATUS, ListStatus.Loading);

    return new Promise<void>((resolve, reject) => {
      if (!ids || !ids.length) {
        return reject(new Error('Invalid data!'));
      }

      resolve();
    })
      .then(() => Promise.all(ids.map(async (id) => api.ads.restore(id))))
      .then(() => {
        dispatch(AdsActions.LOAD_ALL, {
          ...router.currentRoute.params,
          ...router.currentRoute.query,
        });

        return dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('%(count)% ad was restored successfully', { count: ids.length }),
          },
          { root: true },
        );
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        commit(AdsMutations.SET_ALL_STATUS, ListStatus.Failed);

        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },
};